import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
    SuiContainer,
    SuiModalHeader,
    SuiModalContent,
    SuiModalFooter,
    SuiTabs,
    SuiTab,
    SuiSlides,
    SuiSlide,
    SuiButton,
} from '@splashthat/sui';
import CubeLoaderOverlay from 'common/components/CubeLoaderOverlay';
import { MODAL_SIZE } from 'common/components/CubeLoader';
import { colorAppShadedBackground } from 'common/styles/colors';
import { getIsTicketedEvent } from 'common/entities/event/selectors';
import { getEventId } from 'common/store/events/selectors';

import FormTemplateLibraryStock from './FormTemplateLibraryStock';
import FormTemplateLibraryCustom from './FormTemplateLibraryCustom';
import FormTemplateLibraryCategory from './FormTemplateLibraryCategory';

import { getSelectedTemplate } from '../../selectors/templateLibrary';
import { setSelectedTemplateId } from '../../actions/templateLibrary';
import {
    TEMPLATE_QUESTIONS_LABEL,
    MY_QUESTIONS_LABEL,
    CREATE_NEW_LABEL,
    TEMPLATE_QUESTIONS_TAB_VALUE,
    MY_QUESTIONS_TAB_VALUE,
    CREATE_NEW_TAB_VALUE,
    INPUT_TYPE_CATEGORY,
} from '../../constants/templateLibrary';

import './styles.styl';

const TEMPLATE_LIBRARY_TABS = [
    {
        label: TEMPLATE_QUESTIONS_LABEL,
        value: TEMPLATE_QUESTIONS_TAB_VALUE,
    },
    {
        label: MY_QUESTIONS_LABEL,
        value: MY_QUESTIONS_TAB_VALUE,
    },
    {
        label: CREATE_NEW_LABEL,
        value: CREATE_NEW_TAB_VALUE,
    },
];

const CHOOSE_QUESTION_TITLE = 'Choose Question';
const ADD_TO_FORM_BUTTON_LABEL = 'Add to Form';

const baseClassName = 'FormTemplateLibrary';

class FormTemplateLibrary extends Component {
    static propTypes = {
        isLoadingTemplateLibrary: PropTypes.bool,
        activeTab: PropTypes.string,
        closeFormTemplateLibrary: PropTypes.func.isRequired,
        setFormTemplateLibraryView: PropTypes.func.isRequired,
        createNodeFromTemplate: PropTypes.func.isRequired,
        setSelectedTemplateId: PropTypes.func.isRequired,
        selectedTemplate: PropTypes.object,
        loadApiQuestions: PropTypes.func.isRequired,
        alreadyAddedUniqueNames: PropTypes.arrayOf(PropTypes.string).isRequired,
        hasAccessToFormProtectedFields: PropTypes.bool,
        isTicketedEvent: PropTypes.bool.isRequired,
    };

    static defaultProps = {
        closeFormTemplateLibrary: null,
        isLoadingTemplateLibrary: false,
        activeTab: TEMPLATE_QUESTIONS_TAB_VALUE,
        selectedTemplate: null,
        hasAccessToFormProtectedFields: false,
    };

    componentDidMount() {
        this.props.loadApiQuestions();
    }

    handleClickAddToForm = () => {
        if (this.props.selectedTemplate) {
            this.props.createNodeFromTemplate(this.props.selectedTemplate);
            this.props.closeFormTemplateLibrary();
        }
    };

    handleClickTemplate = (event, template = {}) => {
        this.props.setSelectedTemplateId(template.id || null);
    };

    renderModalContent() {
        const { isLoadingTemplateLibrary, activeTab, alreadyAddedUniqueNames } = this.props;

        if (isLoadingTemplateLibrary) {
            return (
                <CubeLoaderOverlay
                    loading={isLoadingTemplateLibrary}
                    backgroundColor={colorAppShadedBackground}
                    size={MODAL_SIZE}
                    delay={0}
                />
            );
        }

        return (
            <SuiSlides slide={activeTab} fitToContainer coverAvailableArea>
                <SuiSlide slide={TEMPLATE_QUESTIONS_TAB_VALUE}>
                    <FormTemplateLibraryStock
                        onClickTemplate={this.handleClickTemplate}
                        alreadyAddedUniqueNames={alreadyAddedUniqueNames}
                    />
                </SuiSlide>
                <SuiSlide slide={MY_QUESTIONS_TAB_VALUE}>
                    <FormTemplateLibraryCustom
                        onClickTemplate={this.handleClickTemplate}
                        alreadyAddedUniqueNames={alreadyAddedUniqueNames}
                    />
                </SuiSlide>
                <SuiSlide slide={CREATE_NEW_TAB_VALUE}>
                    <FormTemplateLibraryCategory
                        category={INPUT_TYPE_CATEGORY}
                        onClickTemplate={this.handleClickTemplate}
                    />
                </SuiSlide>
            </SuiSlides>
        );
    }

    render() {
        return (
            <SuiContainer variation="border standard flex-vertical" className={`${baseClassName} SuiModalContainer`}>
                <SuiModalHeader>
                    <h2 className={`${baseClassName}__headline`}>{CHOOSE_QUESTION_TITLE}</h2>
                    <SuiTabs
                        className={`${baseClassName}__navigation`}
                        value={this.props.activeTab}
                        onChange={(event, value) => {
                            this.props.setFormTemplateLibraryView(value);
                        }}
                    >
                        {[...TEMPLATE_LIBRARY_TABS].filter(Boolean).map((tabProps) => (
                            <SuiTab
                                {...tabProps}
                                key={tabProps.value}
                                autoFocus={tabProps.value === this.props.activeTab}
                                data-qa={`${baseClassName}__${tabProps.value}`}
                            />
                        ))}
                    </SuiTabs>
                </SuiModalHeader>

                <SuiModalContent className={`${baseClassName}__content`}>{this.renderModalContent()}</SuiModalContent>

                <SuiModalFooter>
                    <SuiContainer variation="flex-group">
                        <SuiButton
                            disabled={!this.props.selectedTemplate}
                            label={ADD_TO_FORM_BUTTON_LABEL}
                            onClick={this.handleClickAddToForm}
                            data-qa={`${baseClassName}__add-button`}
                        />
                    </SuiContainer>
                </SuiModalFooter>
            </SuiContainer>
        );
    }
}

const mapStateToProps = (state) => {
    const selectedTemplate = getSelectedTemplate(state);
    const eventId = getEventId(state);
    const isTicketedEvent = getIsTicketedEvent(state, eventId);

    return {
        selectedTemplate,
        isTicketedEvent,
    };
};

const mapDispatchToProps = {
    setSelectedTemplateId,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormTemplateLibrary);
